import { Bell, Menu, Search, Settings } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { useAppSelector } from "../../../hooks/reducer";

const pageMeta: Array<{ match: (path: string) => boolean; title: string; eyebrow: string }> = [
  {
    match: (path) => path.startsWith("/admin/applications"),
    title: "Заявки кандидатів",
    eyebrow: "Адміністрування",
  },
  {
    match: (path) => path.startsWith("/admin/calendar"),
    title: "Календар потоків",
    eyebrow: "Адміністрування",
  },
  {
    match: (path) => path.startsWith("/admin/chat"),
    title: "Чати потоків",
    eyebrow: "Адміністрування",
  },
  {
    match: (path) => path.startsWith("/application-status"),
    title: "Моя заявка",
    eyebrow: "Кандидат МОЖУ",
  },
  {
    match: (path) => path.startsWith("/calendar"),
    title: "Календар активностей",
    eyebrow: "Моя програма",
  },
  {
    match: (path) => path.startsWith("/chat"),
    title: "Чат потоку",
    eyebrow: "Моя програма",
  },
  {
    match: (path) => path.startsWith("/cabinet/yellow-quest"),
    title: "Мій маршрут",
    eyebrow: "Roadmap програми",
  },
  {
    match: (path) => path.startsWith("/courseCreate"),
    title: "Створення курсу",
    eyebrow: "Керування навчанням",
  },
  {
    match: (path) => path.startsWith("/settings"),
    title: "Профіль та налаштування",
    eyebrow: "Обліковий запис",
  },
  {
    match: (path) => path.startsWith("/my-courselist"),
    title: "Мої курси",
    eyebrow: "Особистий кабінет",
  },
];

const Header = (props: {
  sidebarOpen: string | boolean | undefined;
  setSidebarOpen: (open: boolean) => void;
}) => {
  const location = useLocation();
  const user = useAppSelector((state) => state.authReducer.data?.user);
  const currentPage =
    pageMeta.find((item) => item.match(location.pathname)) || pageMeta[pageMeta.length - 1];

  const displayName = user?.username || "Користувач";
  const initials = displayName
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const renderBrandSafeText = (value: string) => {
    if (!value.includes("МОЖУ")) return value;
    const [before, after] = value.split("МОЖУ");
    return (
      <>
        {before}
        <span className="notranslate" translate="no">
          МОЖУ
        </span>
        {after}
      </>
    );
  };

  return (
    <header className="sticky top-0 z-30 h-16 shrink-0 border-b-2 border-black/10 bg-white/88 backdrop-blur-xl sm:h-20">
      <div className="flex h-full items-center justify-between gap-3 px-3 sm:gap-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            aria-controls="sidebar"
            onClick={(event) => {
              event.stopPropagation();
              props.setSidebarOpen(!props.sidebarOpen);
            }}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl border-2 border-black bg-[#ffd400] text-black shadow-[3px_3px_0_#000] transition active:translate-y-0.5 active:shadow-[1px_1px_0_#000] sm:h-11 sm:w-11 lg:hidden"
            aria-label="Відкрити меню"
          >
            <Menu size={20} />
          </button>

          <div className="hidden min-w-0 md:block">
            <p className="truncate text-[11px] font-black uppercase tracking-[0.16em] text-[#8a6f00]">
              {renderBrandSafeText(currentPage.eyebrow)}
            </p>
            <h1 className="truncate text-[22px] font-black leading-tight tracking-[-0.04em] text-black sm:text-[26px]">
              {currentPage.title}
            </h1>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <button
            type="button"
            className="hidden h-11 min-w-[240px] items-center gap-2 rounded-2xl border-2 border-black/10 bg-white px-4 text-left text-sm font-semibold text-[#8a8a8a] transition hover:border-black/20 xl:flex"
            title="Пошук буде підключений пізніше"
          >
            <Search size={17} />
            Пошук у кабінеті
          </button>

          <button
            type="button"
            className="relative grid h-10 w-10 place-items-center rounded-2xl border-2 border-black/10 bg-white text-black transition hover:border-black/25 hover:bg-[#fff8cf] sm:h-11 sm:w-11"
            aria-label="Сповіщення"
            title="Сповіщення"
          >
            <Bell size={18} />
            <span className="absolute right-2 top-2 h-2 w-2 rounded-full border border-white bg-[#ffd400]" />
          </button>

          <Link
            to="/settings"
            className="flex h-10 items-center gap-2 rounded-2xl border-2 border-black/10 bg-white px-1.5 text-black transition hover:border-black/25 hover:bg-[#fff8cf] sm:h-11 sm:px-2.5"
            title="Налаштування профілю"
          >
            <div className="grid h-7 w-7 place-items-center rounded-xl border-2 border-black bg-[#ffd400] text-[11px] font-black text-black sm:h-8 sm:w-8 sm:text-xs">
              {initials}
            </div>
            <span className="hidden max-w-[130px] truncate text-sm font-black lg:block">
              {displayName}
            </span>
            <Settings size={15} className="hidden text-[#8a8a8a] lg:block" />
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Header;
